import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';

import type { CompanyListSort, CompanyResponse, SortDirection } from '../../api';
import {
  AsyncSection,
  DataTable,
  PageHeader,
  Pagination,
  Panel,
  type Column,
  type ColumnFilterKind,
  type ColumnFiltersState,
  type SortingState,
} from '../../components';
import { formatCnpj, formatDate } from '../../format';
import { PATHS, staffCompanyPath } from '../../routes/paths';
import { useCompaniesPage, type CompanyColumnFilters } from '../queries';
import { GROUPS_COPY } from './groups';
import { pageFrom, SEARCH_DEBOUNCE_MS } from './listAddress';
import { ONBOARDING_COPY } from './onboarding';

/**
 * Who is already a customer. Nothing else.
 *
 * The screen a menu entry leads to answers one question, and registering a
 * company is a different one: the form now lives at
 * [`/equipe/empresas/nova`](./NewCompanyPage.tsx), behind the action in the
 * header. A form sitting permanently above a list is an invitation to start
 * typing into it while reading, and it pushes the answer somebody came for
 * below the fold on every visit.
 *
 * A row is a link (`DataTable`'s `rowLink`): the name is the anchor, and the
 * rest of the line takes the same click.
 *
 * Opening a company navigates to its own address rather than swapping the view
 * out from under the URL, so a company can be bookmarked, reloaded and pasted
 * into a chat -- see [`CompanyDetailPage`](./CompanyDetailPage.tsx), which
 * carries the opaque uuid and nothing else.
 *
 * Insurers used to be a panel at the bottom of this screen. They are their own
 * pair of addresses now, next door in the menu: a contract points at one, so
 * needing to register one is a thing that happens, and a panel halfway down
 * another screen is a thing people learn by accident.
 *
 * One page at a time (#427), through `useCompaniesPage` rather than the
 * whole-registry `useCompanies()` the selects elsewhere rely on.
 *
 * Since #439 the list is the shared table with its filter row and sortable
 * headers, in manual mode: the server filters and sorts, and the table only
 * reports what was typed or clicked. The filters, the sort and the page live
 * in the address -- `?nome=`, `?identificador=`, `?cnpj=`, `?grupo=`,
 * `?ordem=` and `?sentido=desc`, and `?pagina=`, 1-based, each omitted at its
 * default -- so a reload or a pasted link lands on the same view, the way
 * `?ciclo=` does on the submission screen. Typing in a filter is debounced:
 * the request goes out when the person pauses, and a new filter starts again
 * on the first page.
 *
 * There is no free-text search box above the table any more (#447): it
 * predated the filter row and only covered columns that have a filter of
 * their own. An old link's `?busca=` is ignored, and dropped by the next
 * address this screen writes.
 *
 * Reading this needs `company:read` and the create screen needs
 * `company:write`, neither of which a customer role or a machine credential
 * holds. The router keeps a customer's HR user out of the address; the server
 * is what keeps them out of the data.
 */
export function CompaniesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const texts = useMemo(() => textsFrom(searchParams), [searchParams]);
  const sort = useMemo(() => sortFrom(searchParams), [searchParams]);
  const page = pageFrom(searchParams.get('pagina'));
  const filters = columnFiltersOf(texts);
  const companies = useCompaniesPage('', page, {
    filters,
    ...(sort !== undefined && { sort }),
  });

  // What is in the filter row, which runs ahead of the address by the
  // debounce.
  const [draft, setDraft] = useState(texts);
  // The texts this screen last wrote to the address. A change that is not this
  // one came from outside -- back, forward, the clear button -- and the inputs
  // follow it; one that is, is the inputs' own text arriving, and copying it
  // back would undo whatever was typed during the debounce.
  const written = useRef(texts);
  useEffect(() => {
    if (!sameTexts(texts, written.current)) {
      written.current = texts;
      setDraft(texts);
    }
  }, [texts]);

  useEffect(() => {
    const next = trimmed(draft);
    if (sameTexts(next, texts)) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      written.current = next;
      // Replace rather than push: every pause while typing is not a place
      // somebody wants Voltar to step back through.
      setSearchParams(listAddress(next, sort, 1), { replace: true });
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [draft, texts, sort, setSearchParams]);

  const goToPage = (next: number) => setSearchParams(listAddress(texts, sort, next));
  // The filters go, the sort stays: it narrows nothing.
  const clearNarrowing = () => setSearchParams(listAddress(NO_TEXTS, sort, 1));
  const changeSort = (sorting: SortingState) =>
    setSearchParams(listAddress(texts, sortOf(sorting), 1));
  const changeFilters = (next: ColumnFiltersState) => {
    const value = (key: CompanyFilterKey) => next.find((entry) => entry.id === key)?.value ?? '';
    setDraft({
      nome: value('name'),
      identificador: value('slug'),
      cnpj: value('cnpj'),
      grupo: value('group'),
    });
  };

  const draftFilters: ColumnFiltersState = FILTER_KEYS.flatMap((key) => {
    const value = draft[FILTER_PARAMS[key]];
    return value === '' ? [] : [{ id: key, value }];
  });
  const sorting: SortingState =
    sort === undefined ? [] : [{ id: sort.field, desc: sort.direction === 'desc' }];
  const columnFiltering = Object.keys(filters).length > 0;
  // Whether the rows on screen answer column filters. While the next page
  // loads, the rows are still the previous request's (`keepPreviousData`), so
  // this holds the previous answer until the new one arrives: erasing a filter
  // that matched nothing keeps the empty table and its filter row, marked
  // busy, instead of flashing the empty-registry state for the unfiltered
  // request still in flight. Set during render, React's pattern for state that
  // follows a prop, so no frame shows the stale value.
  const [rowsFiltered, setRowsFiltered] = useState(columnFiltering);
  if (companies.isSuccess && !companies.isPlaceholderData && rowsFiltered !== columnFiltering) {
    setRowsFiltered(columnFiltering);
  }
  const textFilter: ColumnFilterKind = {
    kind: 'text',
    placeholder: ONBOARDING_COPY.companiesFilterPlaceholder,
  };

  const columns: readonly Column<CompanyResponse>[] = [
    // The accessors only switch on sorting and filtering in the table; with
    // `manual`, the rows arrive in the server's order and are never compared.
    {
      key: 'name',
      header: ONBOARDING_COPY.companyNameLabel,
      render: (row) => row.name,
      accessor: (row) => row.name,
      sortable: true,
      filter: textFilter,
    },
    {
      key: 'slug',
      header: ONBOARDING_COPY.companySlugLabel,
      render: (row) => <code>{row.slug}</code>,
      accessor: (row) => row.slug,
      sortable: true,
      filter: textFilter,
    },
    {
      key: 'cnpj',
      header: ONBOARDING_COPY.companyCnpjLabel,
      // A company registered before the field existed renders as the same em
      // dash every other empty cell uses, rather than as a blank somebody has
      // to interpret. Opening it is how it gets one.
      render: (row) => <code>{formatCnpj(row.cnpj)}</code>,
      accessor: (row) => row.cnpj,
      sortable: true,
      filter: textFilter,
    },
    {
      key: 'group',
      header: GROUPS_COPY.groupLabel,
      // Blank rather than the em dash, unlike the CNPJ: a company without a
      // group is not missing anything, and most companies are in none.
      render: (row) => row.group?.name ?? '',
      accessor: (row) => row.group?.name,
      sortable: true,
      filter: textFilter,
    },
    {
      key: 'created_at',
      header: ONBOARDING_COPY.companyCreatedAtLabel,
      align: 'end',
      render: (row) => formatDate(row.created_at),
      // Sortable, not filtered: a date range is a control of its own, for later.
      accessor: (row) => row.created_at,
      sortable: true,
    },
  ];

  return (
    <div className="page">
      <PageHeader
        title={ONBOARDING_COPY.title}
        description={ONBOARDING_COPY.description}
        actions={
          <Link className="button button--primary" to={PATHS.staffCompanyNew}>
            {ONBOARDING_COPY.registerCompany}
          </Link>
        }
      />

      <Panel
        title={ONBOARDING_COPY.companiesTitle}
        description={ONBOARDING_COPY.companiesDescription}
      >
        <AsyncSection
          state={companies}
          loadingLabel={ONBOARDING_COPY.companiesLoading}
          errorTitle={ONBOARDING_COPY.companiesError}
          // Two different empties, told apart: a registry with nothing in it,
          // and a page past the end of a list that does have companies. A
          // column filter that matches nothing is the third, and it is the
          // table's: the filter row stays on screen, where the filter that
          // emptied it can be changed.
          isEmpty={(data) => data.items.length === 0 && !(rowsFiltered && data.count === 0)}
          {...emptyState(page, companies.data?.count ?? 0, goToPage)}
        >
          {(data) => (
            <div aria-busy={companies.isPlaceholderData}>
              <DataTable
                id="companies"
                caption={ONBOARDING_COPY.companiesCaption}
                columns={columns}
                rows={data.items}
                rowKey={(row) => row.id}
                // Opening a company is the only thing a row does, so the row is
                // the link: the name is the anchor -- it can be opened in a new
                // tab, copied, and read by anything that lists a page's
                // destinations -- and a click anywhere else on the line goes to
                // the same place.
                rowLink={(row) => staffCompanyPath(row.id)}
                manual
                sorting={sorting}
                onSortingChange={changeSort}
                columnFilters={draftFilters}
                onColumnFiltersChange={changeFilters}
                filtered={rowsFiltered}
                noMatchTitle={ONBOARDING_COPY.noMatchingFilteredCompanies}
                noMatchDescription={ONBOARDING_COPY.noMatchingFilteredCompaniesDescription}
                noMatchAction={
                  <button
                    type="button"
                    className="button button--secondary"
                    onClick={clearNarrowing}
                  >
                    {ONBOARDING_COPY.clearCompaniesFilters}
                  </button>
                }
              />
              <Pagination
                page={page}
                pageSize={data.limit ?? data.items.length}
                total={data.count}
                onPageChange={goToPage}
              />
            </div>
          )}
        </AsyncSection>
      </Panel>
    </div>
  );
}

/** The columns with a text filter, by the names the table and the API use. */
type CompanyFilterKey = keyof CompanyColumnFilters;
const FILTER_KEYS: readonly CompanyFilterKey[] = ['name', 'slug', 'cnpj', 'group'];

/** The typed-in values the address carries, under their Portuguese keys. */
interface ListTexts {
  readonly nome: string;
  readonly identificador: string;
  readonly cnpj: string;
  readonly grupo: string;
}
type TextParam = keyof ListTexts;
const TEXT_PARAMS: readonly TextParam[] = ['nome', 'identificador', 'cnpj', 'grupo'];
const NO_TEXTS: ListTexts = { nome: '', identificador: '', cnpj: '', grupo: '' };

/** Each filtered column's key in the address. */
const FILTER_PARAMS: Record<CompanyFilterKey, TextParam> = {
  name: 'nome',
  slug: 'identificador',
  cnpj: 'cnpj',
  group: 'grupo',
};

/** `?ordem=`'s value for each column the API sorts by. */
const SORT_PARAMS: Record<CompanyListSort, string> = {
  name: 'nome',
  slug: 'identificador',
  cnpj: 'cnpj',
  group: 'grupo',
  created_at: 'cadastrada',
};

type ListSort = { readonly field: CompanyListSort; readonly direction: SortDirection } | undefined;

function textsFrom(params: URLSearchParams): ListTexts {
  return {
    nome: params.get('nome') ?? '',
    identificador: params.get('identificador') ?? '',
    cnpj: params.get('cnpj') ?? '',
    grupo: params.get('grupo') ?? '',
  };
}

function sameTexts(left: ListTexts, right: ListTexts): boolean {
  return TEXT_PARAMS.every((key) => left[key] === right[key]);
}

function trimmed(texts: ListTexts): ListTexts {
  return {
    nome: texts.nome.trim(),
    identificador: texts.identificador.trim(),
    cnpj: texts.cnpj.trim(),
    grupo: texts.grupo.trim(),
  };
}

/** The non-blank column filters, as `useCompaniesPage` sends them. */
function columnFiltersOf(texts: ListTexts): CompanyColumnFilters {
  return Object.fromEntries(
    FILTER_KEYS.flatMap((key) => {
      const value = texts[FILTER_PARAMS[key]];
      return value === '' ? [] : [[key, value]];
    }),
  );
}

/** `?ordem=` and `?sentido=`: an unknown column is no sort, anything but `desc` ascends. */
function sortFrom(params: URLSearchParams): ListSort {
  const ordem = params.get('ordem');
  const field = (Object.keys(SORT_PARAMS) as CompanyListSort[]).find(
    (key) => SORT_PARAMS[key] === ordem,
  );
  if (field === undefined) {
    return undefined;
  }
  return { field, direction: params.get('sentido') === 'desc' ? 'desc' : 'asc' };
}

/** The table's sorting as the list's; the table only ever holds the API's column keys. */
function sortOf(sorting: SortingState): ListSort {
  const first = sorting[0];
  if (first === undefined || !(first.id in SORT_PARAMS)) {
    return undefined;
  }
  return { field: first.id as CompanyListSort, direction: first.desc ? 'desc' : 'asc' };
}

/** The list's query string, with each value left out at its default. */
function listAddress(texts: ListTexts, sort: ListSort, page: number): URLSearchParams {
  const params = new URLSearchParams();
  for (const key of TEXT_PARAMS) {
    if (texts[key] !== '') {
      params.set(key, texts[key]);
    }
  }
  if (sort !== undefined) {
    params.set('ordem', SORT_PARAMS[sort.field]);
    if (sort.direction === 'desc') {
      params.set('sentido', 'desc');
    }
  }
  if (page > 1) {
    params.set('pagina', String(page));
  }
  return params;
}

function emptyState(page: number, count: number, goToPage: (page: number) => void) {
  if (count > 0 && page > 1) {
    return {
      emptyTitle: ONBOARDING_COPY.companiesPageOutOfRange,
      emptyDescription: ONBOARDING_COPY.companiesPageOutOfRangeDescription,
      emptyAction: (
        <button type="button" className="button button--secondary" onClick={() => goToPage(1)}>
          {ONBOARDING_COPY.companiesFirstPage}
        </button>
      ),
    };
  }
  return {
    emptyTitle: ONBOARDING_COPY.noCompanies,
    emptyDescription: ONBOARDING_COPY.noCompaniesDescription,
  };
}
